import { useState, useEffect } from 'react';
import Header from '../Components/Header/header';
import { useNavigate } from 'react-router-dom';
import config from '../Context/Config';
import { checkTokenAndNavigate } from "../Utils/checkNavigate";
import { refreshToken } from '../Context/RefreshToken';
import Swal from "sweetalert2";
import Modal from "react-modal";
import usePinLock from '../Utils/pinLock'; // Import the PIN lock hook


Modal.setAppElement("#root");

const Admin = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState([]);
    const [filteredData, setFilteredData] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingData, setEditingData] = useState(null);
    const [editType, setEditType] = useState('');
    const [filterName, setFilterName] = useState('');
    const [filterLocation, setFilterLocation] = useState('');
    const [filterType, setFilterType] = useState('');
    const [filterPractice, setFilterPractice] = useState('');
    const [uniqueLocations, setUniqueLocations] = useState([]);
    const [uniqueTypes, setUniqueTypes] = useState([]);
    const [uniquePractices, setUniquePractices] = useState([]);
    const [refreshKey, setRefreshKey] = useState(0);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [availableWorkers, setAvailableWorkers] = useState([]);
    const [selectedWorker, setSelectedWorker] = useState(null);
    const [addFormData, setAddFormData] = useState({ workerSearch: '', name: '', email: '', location: '', practice: '', type: '' });
    const [filteredWorkers, setFilteredWorkers] = useState([]);

    const tryRefresh = async (autoReloadBool) => {
        await refreshToken({
            navigate,
            onSuccess: (token) => console.log("Refreshed:", token),
            onError: (err) => console.error("Failed to refresh", err),
            autoReload: autoReloadBool
        });
    };

    const { isLocked } = usePinLock(tryRefresh);


    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                // Build query params for filters
                const queryParams = new URLSearchParams();
                if (filterName) queryParams.append('name', filterName);
                if (filterLocation) queryParams.append('location', filterLocation);
                if (filterType) queryParams.append('type', filterType);
                if (filterPractice) queryParams.append('practice', filterPractice);

                const response = await fetch(`${config.API_BASE_URL}/admin/list${queryParams.toString() ? `?${queryParams.toString()}` : ''}`, {
                    method: "GET",
                    headers: {
                        "action_id": config.ACTIONID['view'],
                        "Content-Type": "application/json",
                        "Authorization": `${localStorage.getItem("digital-form-idtoken")}`,
                        "practice": localStorage.getItem("digital-form-dept-practice")
                    }
                });

                if (!response.ok) {
                    switch (response.status) {
                        case 401:
                            Swal.fire({
                                icon: "warning",
                                title: "Session Expired",
                                text: "Your session has expired. Please click on 'Restore Session'.",
                                confirmButtonText: "Restore Session",
                            }).then(() => tryRefresh(true));
                            break;
                        case 403:
                            Swal.fire({
                                icon: "error",
                                title: "Access Denied",
                                text: "You don't have permission to view this page."
                            });
                            break;
                        default:
                            Swal.fire({
                                icon: "error",
                                title: "Error",
                                text: "An error occurred while fetching data."
                            });
                    }
                    return;
                }

                const result = await response.json();
                const dataArray = Array.isArray(result) ? result : result.data || [];
                setData(dataArray);
                setFilteredData(dataArray);

                // Extract unique locations, types, and practices only when no filters are applied
                const hasActiveFilters = filterName || filterLocation || filterType || filterPractice;
                if (!hasActiveFilters) {
                    const locations = [...new Set(dataArray.map(item => item.location).filter(Boolean))];
                    const types = [...new Set(dataArray.map(item => item.type).filter(Boolean))];
                    const practices = [...new Set(dataArray.map(item => item.practice).filter(Boolean))];
                    setUniqueLocations(locations);
                    setUniqueTypes(types);
                    setUniquePractices(practices);
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                Swal.fire({
                    icon: "error",
                    title: "Error",
                    text: "Failed to fetch data. Please try again later."
                });
            } finally {
                setIsLoading(false);
            }
        };

        checkTokenAndNavigate(fetchData, tryRefresh, navigate);
    }, [refreshKey, filterName, filterLocation, filterType, filterPractice]);

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const sortData = (dataToSort, key, direction) => {
        const sorted = [...dataToSort];
        if (!key) return sorted;

        sorted.sort((a, b) => {
            const aVal = a[key] || '';
            const bVal = b[key] || '';

            if (aVal < bVal) return direction === 'asc' ? -1 : 1;
            if (aVal > bVal) return direction === 'asc' ? 1 : -1;
            return 0;
        });

        return sorted;
    };

    const applyFilters = (name = filterName, location = filterLocation, type = filterType, practice = filterPractice) => {
        const filtered = data.filter(item => {
            const matchName = !name || item.name?.toLowerCase().includes(name.toLowerCase());
            const matchLocation = !location || item.location === location;
            const matchType = !type || item.type === type;
            const matchPractice = !practice || item.practice === practice;
            return matchName && matchLocation && matchType && matchPractice;
        });
        setFilteredData(filtered);
    };

    const handleLocationChange = (value) => {
        setFilterLocation(value);
        applyFilters(filterName, value, filterType, filterPractice);
    };

    const handleTypeChange = (value) => {
        setFilterType(value);
        applyFilters(filterName, filterLocation, value, filterPractice);
    };

    const handlePracticeChange = (value) => {
        setFilterPractice(value);
        applyFilters(filterName, filterLocation, filterType, value);
    };

    const handleSearch = () => {
        applyFilters(filterName, filterLocation, filterType, filterPractice);
    };

    const handleUndo = () => {
        setFilterName('');
        setFilterLocation('');
        setFilterType('');
        setFilterPractice('');
        setFilteredData(data);
    };

    const handleAddClick = async () => {
        try {
            setIsLoading(true);
            const response = await fetch(`${config.API_BASE_URL}/admin/add`, {
                method: "POST",
                headers: {
                    "action_id": config.ACTIONID['view'],
                    "Content-Type": "application/json",
                    "Authorization": `${localStorage.getItem("digital-form-idtoken")}`,
                    "practice": localStorage.getItem("digital-form-dept-practice")
                },
                body: JSON.stringify({})
            });

            if (!response.ok) {
                throw new Error('Failed to fetch available workers');
            }

            const workers = await response.json();
            setAvailableWorkers(workers);
            setFilteredWorkers(workers);
            setSelectedWorker(null);
            setAddFormData({ workerSearch: '', name: '', email: '', location: '', practice: '', type: '' });
            setIsAddModalOpen(true);
        } catch (error) {
            console.error("Error fetching available workers:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Only Admin can add users. Please contact your administrator."
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleWorkerSearch = (searchTerm) => {
        setAddFormData({ ...addFormData, workerSearch: searchTerm });
        if (searchTerm.trim()) {
            const filtered = availableWorkers.filter(worker =>
                worker.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                worker.email.toLowerCase().includes(searchTerm.toLowerCase())
            );
            setFilteredWorkers(filtered);
        } else {
            setFilteredWorkers(availableWorkers);
        }
    };

    const handleWorkerSelect = (worker) => {
        setSelectedWorker(worker);
        setAddFormData({
            workerSearch: '',
            name: worker.name,
            email: worker.email,
            location: '',
            practice: '',
            type: ''
        });
        setFilteredWorkers([]);
    };

    const handleAddSave = async () => {
        if (!selectedWorker || !addFormData.location || !addFormData.practice || !addFormData.type) {
            Swal.fire({
                icon: "warning",
                title: "Incomplete Form",
                text: "Please fill all required fields."
            });
            return;
        }

        try {
            setIsLoading(true);
            const payload = {
                name: selectedWorker.name,
                email: selectedWorker.email,
                location: addFormData.location,
                practice: addFormData.practice,
                type: addFormData.type
            };

            const response = await fetch(`${config.API_BASE_URL}/admin/add`, {
                method: "POST",
                headers: {
                    "action_id": config.ACTIONID['edit'],
                    "Content-Type": "application/json",
                    "Authorization": `${localStorage.getItem("digital-form-idtoken")}`,
                    "practice": localStorage.getItem("digital-form-dept-practice")
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                throw new Error('Failed to add user');
            }

            const newUser = await response.json();
            setData([...data, newUser]);
            setFilteredData([...data, newUser]);
            setIsAddModalOpen(false);
            Swal.fire({
                icon: "success",
                title: "Added",
                text: "User added successfully."
            });
        } catch (error) {
            console.error("Error adding user:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to add user. Please try again."
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditClick = (item) => {
        setEditingData(item);
        setEditType(item.type || '');
        setIsEditModalOpen(true);
    };

    const handleSaveEdit = async () => {
        if (!editingData) return;

        try {
            setIsLoading(true);
            const response = await fetch(`${config.API_BASE_URL}/admin/update`, {
                method: "PUT",
                headers: {
                    "action_id": config.ACTIONID['edit'],
                    "Content-Type": "application/json",
                    "Authorization": `${localStorage.getItem("digital-form-idtoken")}`,
                    "practice": localStorage.getItem("digital-form-dept-practice")
                },
                body: JSON.stringify({
                    id: editingData.id,
                    type: editType,
                    practice: editingData.practice
                })
            });

            if (!response.ok) {
                throw new Error('Failed to update');
            }

            const updatedItem = { ...editingData, type: editType };
            const updatedData = data.map(item => item.id === editingData.id ? updatedItem : item);
            setData(updatedData);
            setFilteredData(updatedData);
            setIsEditModalOpen(false);
            Swal.fire({
                icon: "success",
                title: "Updated",
                text: "Type updated successfully."
            });
        } catch (error) {
            console.error("Error updating:", error);
            Swal.fire({
                icon: "error",
                title: "Error",
                text: "Failed to update. Please try again."
            });
        } finally {
            setIsLoading(false);
        }
    };

    const sortedData = sortData(filteredData, sortConfig.key, sortConfig.direction);

    return (
        <div id='admin-page' className={`min-h-screen flex flex-col ${isLocked ? 'blur-2xl' : ''}`}>
            {isLoading && (
                <div className="loader-overlay flex items-center justify-center fixed top-0 left-0 w-full h-full bg-black bg-opacity-30 z-50">
                    <div className="loader"></div>
                </div>
            )}

            <Header pageTitle="Admin" />

            <div className="bg-white px-4 sm:px-6 py-4 border-b border-gray-200">
                <div className="flex flex-col gap-3">
                    <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Search by Name or Chart</p>
                    <div className="flex flex-wrap items-center gap-3">
                        <input
                            type="text"
                            id="searchName"
                            placeholder="Search"
                            value={filterName}
                            onChange={(e) => setFilterName(e.target.value)}
                            className="flex-1 min-w-[200px] px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />

                        <button
                            id="search"
                            type="button"
                            onClick={handleSearch}
                            className="px-5 py-2.5 border-2 border-blue-600 text-blue-600 text-sm font-semibold rounded-lg hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 flex items-center gap-2 transition-colors"
                        >
                            <i className="fas fa-search"></i> Search
                        </button>

                        <button
                            type="button"
                            onClick={() => setRefreshKey(prevKey => prevKey + 1)}
                            className="px-5 py-2.5 border-2 border-blue-600 text-blue-600 text-sm font-semibold rounded-lg hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 flex items-center gap-2 transition-colors"
                        >
                            <i className="fas fa-sync-alt"></i> Refresh
                        </button>

                        <button
                            type="button"
                            onClick={handleAddClick}
                            className="px-5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-opacity-50 flex items-center gap-2 transition-colors"
                        >
                            <i className="fas fa-plus"></i> Add
                        </button>

                        {/* Location Dropdown */}
                        <div className="flex items-center gap-2">
                            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide flex items-center gap-1">
                                <i className="fas fa-map-marker-alt text-blue-600"></i>
                                Location
                            </p>
                            <select
                                value={filterLocation}
                                onChange={(e) => handleLocationChange(e.target.value)}
                                className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[150px]"
                            >
                                <option value="">All Locations</option>
                                {uniqueLocations.map((location) => (
                                    <option key={location} value={location}>
                                        {location}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Practice Dropdown */}
                        <div className="flex items-center gap-2">
                            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide flex items-center gap-1">
                                <i className="fas fa-hospital text-blue-600"></i>
                                Practice
                            </p>
                            <select
                                value={filterPractice}
                                onChange={(e) => handlePracticeChange(e.target.value)}
                                className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[150px]"
                            >
                                <option value="">All Practices</option>
                                {uniquePractices.map((practice) => (
                                    <option key={practice} value={practice}>
                                        {practice.toUpperCase()}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Type Dropdown */}
                        <div className="flex items-center gap-2">
                            <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide flex items-center gap-1">
                                <i className="fas fa-tag text-blue-600"></i>
                                Type
                            </p>
                            <select
                                value={filterType}
                                onChange={(e) => handleTypeChange(e.target.value)}
                                className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent min-w-[150px]"
                            >
                                <option value="">All Types</option>
                                {uniqueTypes.map((type) => (
                                    <option key={type} value={type}>
                                        {type === 'fdtm' ? 'Front Desk' : type === 'surgical' ? 'Surgical' : type}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {(filterName || filterLocation || filterType || filterPractice) && (
                            <button
                                type="button"
                                onClick={handleUndo}
                                className="px-4 py-2.5 text-gray-700 text-sm font-medium border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50 flex items-center gap-2 transition-colors"
                            >
                                <i className="fas fa-undo"></i> Undo
                            </button>
                        )}
                    </div>

                    <p className="text-xs text-gray-500">
                        Showing {sortedData.length} of {data.length} locations
                    </p>
                </div>
            </div>

            <div className="relative overflow-auto max-h-[70vh] modern-scroll bg-white rounded-xl shadow-lg border border-gray-200 my-3 mx-2">
                <table className="min-w-full divide-y divide-gray-200 table-fixed border-separate" style={{ borderSpacing: '0 12px' }}>
                    <thead className="sticky top-0 z-30 bg-blue-600 text-center">
                        <tr>
                            {[
                                { key: 'name', label: 'Name' },
                                { key: 'location', label: 'Location' },
                                { key: 'practice', label: 'Practice' },
                                { key: 'type', label: 'Type' },
                                { key: 'code', label: 'Code' }
                            ].map(({ key, label }) => (
                                <th
                                    key={key}
                                    onClick={() => handleSort(key)}
                                    className="px-4 py-3 text-center text-sm font-semibold text-white uppercase tracking-wide cursor-pointer hover:bg-blue-700 transition-colors sticky top-0 z-10 bg-blue-600"
                                >
                                    {label} {sortConfig.key === key && (sortConfig.direction === 'asc' ? '↑' : '↓')}
                                </th>
                            ))}
                            <th className="px-4 py-3 text-center text-sm font-semibold text-white uppercase tracking-wide sticky top-0 bg-blue-600 z-10">
                                Action
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-transparent text-center space-y-3">
                        {sortedData.length > 0 ? (
                            sortedData.map((item, index) => (
                                <tr
                                    key={index}
                                    className="bg-white border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200 shadow-md hover:shadow-lg m-2"
                                >
                                    <td className="px-4 py-4 text-sm text-gray-900">
                                        <div className="flex items-center justify-center gap-2.5">
                                            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600">
                                                <i className="fas fa-building text-xs"></i>
                                            </div>
                                            <span className="font-semibold text-gray-900">{item.name || '-'}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-900">
                                        <div className="flex items-center justify-center gap-2.5">
                                            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600">
                                                <i className="fas fa-map-marker-alt text-xs"></i>
                                            </div>
                                            <span className="font-semibold text-gray-900">{item.location || '-'}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-900">
                                        <div className="flex items-center justify-center gap-2.5">
                                            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 text-purple-600">
                                                <i className="fas fa-hospital text-xs"></i>
                                            </div>
                                            <span className="font-semibold text-gray-900 uppercase">{item.practice || '-'}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-900">
                                        <div className="flex items-center justify-center gap-2.5">
                                            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-100 text-blue-600">
                                                <i className="fas fa-tag text-xs"></i>
                                            </div>
                                            <span className="font-semibold text-gray-900">
                                                {item.type === 'fdtm' ? 'Front Desk' : item.type === 'surgical' ? 'Surgical' : item.type || '-'}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4 text-sm text-gray-900">
                                        <div className="flex items-center justify-center gap-2.5">
                                            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 text-purple-600">
                                                <i className="fas fa-barcode text-xs"></i>
                                            </div>
                                            <span className="font-semibold text-gray-900">{item.code || '-'}</span>
                                        </div>
                                    </td>
                                    <td className="px-4 py-4 text-center">
                                        <button
                                            onClick={() => handleEditClick(item)}
                                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200"
                                        >
                                            <i className="fas fa-edit"></i>
                                            <span>Edit</span>
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="6" className="px-4 py-8 text-center text-gray-500">
                                    <div className="flex flex-col items-center gap-2">
                                        <i className="fas fa-inbox text-3xl text-gray-300"></i>
                                        <span className="text-sm font-medium">No data found</span>
                                    </div>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <Modal
                isOpen={isEditModalOpen}
                onRequestClose={() => setIsEditModalOpen(false)}
                className="Modal fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 bg-white rounded-lg shadow-xl p-6 z-50"
                overlayClassName="Overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40"
            >
                <div className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-900 mb-4">Edit User</h2>

                    <div className="space-y-3">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                            <input
                                type="text"
                                value={editingData?.name || ''}
                                disabled
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed text-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                            <input
                                type="text"
                                value={editingData?.location || ''}
                                disabled
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed text-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                            <select
                                value={editType}
                                onChange={(e) => setEditType(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                            >
                                <option value="">Select Type</option>
                                <option value="fdtm">Front Desk</option>
                                <option value="surgical">Surgical</option>
                            </select>
                        </div>
                    </div>

                    <div className="flex gap-3 pt-6">
                        <button
                            onClick={() => setIsEditModalOpen(false)}
                            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors text-sm"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveEdit}
                            disabled={isLoading}
                            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 transition-colors text-sm"
                        >
                            Save
                        </button>
                    </div>
                </div>
            </Modal>

            <Modal
                isOpen={isAddModalOpen}
                onRequestClose={() => setIsAddModalOpen(false)}
                className="Modal fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-2xl bg-white rounded-lg shadow-xl p-8 max-h-[90vh] overflow-y-auto z-50"
                overlayClassName="Overlay fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40"
            >
                <div className="space-y-4">
                    <h2 className="text-xl font-bold text-gray-900">Add New User</h2>

                    <div className="relative">
                        <label className="block text-sm font-medium text-gray-700 mb-1">Search Worker *</label>
                        <input
                            type="text"
                            value={addFormData.workerSearch}
                            onChange={(e) => handleWorkerSearch(e.target.value)}
                            placeholder="Search by name or email"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {addFormData.workerSearch && filteredWorkers.length > 0 && (
                            <div className="absolute top-full left-0 right-0 mt-1 border border-gray-300 rounded-lg bg-white shadow-lg z-10 max-h-48 overflow-y-auto">
                                {filteredWorkers.map((worker, idx) => (
                                    <div
                                        key={idx}
                                        onClick={() => handleWorkerSelect(worker)}
                                        className="p-3 border-b border-gray-200 hover:bg-blue-50 cursor-pointer transition-colors"
                                    >
                                        <div className="font-semibold text-gray-900">{worker.name}</div>
                                        <div className="text-sm text-gray-600">{worker.email}</div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Name</label>
                        <input
                            type="text"
                            value={addFormData.name}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <input
                            type="text"
                            value={addFormData.email}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Location *</label>
                        <select
                            value={addFormData.location}
                            onChange={(e) => {
                                const location = e.target.value;
                                const practice = location ? location.substring(0, 3).toLowerCase() : '';
                                setAddFormData({ ...addFormData, location, practice });
                            }}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Select Location</option>
                            {uniqueLocations.map((location) => (
                                <option key={location} value={location}>
                                    {location}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Practice</label>
                        <input
                            type="text"
                            value={addFormData.practice}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 cursor-not-allowed"
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Type *</label>
                        <select
                            value={addFormData.type}
                            onChange={(e) => setAddFormData({ ...addFormData, type: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Select Type</option>
                            <option value="fdtm">Front Desk</option>
                            <option value="surgical">Surgical</option>
                        </select>
                    </div>

                    <div className="flex gap-3 pt-4">
                        <button
                            onClick={() => setIsAddModalOpen(false)}
                            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleAddSave}
                            disabled={isLoading}
                            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium disabled:opacity-50 transition-colors"
                        >
                            Add
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default Admin;
